import React from 'react';
import { CheckCircle2, FileText, Mail, Sparkles, Briefcase } from 'lucide-react';

/**
 * components/workspace/SectionNav.jsx — RC 1.49.7 Phase 0
 *
 * Horizontal section indicator for the Application Workspace.
 * Replaces the modal-era `StepIndicator` from
 * `JobApplicationWizard.jsx`.
 *
 * URL state contract:
 *   ?section=cv | letter | interview | pack
 *
 * RC 1.50 R.1 (2026-02-09) — the `fit` slug is RETIRED from the
 * Workspace. Baseline Analyse Fit remains on the job-context page
 * (JobDetailPage). The Workspace's third section is now Interview
 * Prep, which internally still consumes the Path C interview-prep
 * map produced by the existing analyse_fit run (no new chargeable
 * action). `?section=fit` URLs are forwarded to `?section=interview`
 * by ApplicationWorkspacePage for deep-link continuity.
 *
 * Click on any tab calls `onSelect(slug)` which the parent page
 * uses to update `setSearchParams({ section: slug }, { replace: true })`.
 */
const SECTIONS = [
  { slug: 'cv',        label: 'CV',               shortLabel: 'CV',        Icon: FileText },
  { slug: 'letter',    label: 'Cover Letter',     shortLabel: 'Letter',    Icon: Mail },
  { slug: 'interview', label: 'Interview Prep',   shortLabel: 'Prep',      Icon: Sparkles },
  { slug: 'pack',      label: 'Application Pack', shortLabel: 'Pack',      Icon: Briefcase },
];

export const WORKSPACE_SECTIONS = SECTIONS.map((s) => s.slug);

export default function SectionNav({
  activeSlug,
  onSelect,
  completed = {},
  furthestIndex,
  disabled = false,
}) {
  const activeIdx = SECTIONS.findIndex((s) => s.slug === activeSlug);
  // RC 1.49.7 Slice 4 — Forward-only navigation (operator P0,
  // 2026-02). When the parent page provides `furthestIndex`,
  // forward-skip buttons (idx > furthestIndex) are disabled. The
  // active section + completed sections (idx ≤ furthestIndex) stay
  // clickable so the user can revisit a completed section as a
  // read-only summary (Slice-3 disclosure cards). When the prop is
  // omitted, the nav falls back to the legacy "all clickable"
  // behaviour for backward compatibility with any test harness.
  // RC 1.50 Workspace UX Slice 0 — `disabled` is a global gate the
  // parent flips ON while any section is mid-flight (generate /
  // run / create). All pills become unclickable, preventing the
  // user from stranding themselves mid-generation.
  const gateActive = typeof furthestIndex === 'number';
  return (
    <nav
      aria-label="Application workspace sections"
      data-testid="workspace-section-nav"
      /* RC 1.55 P0 Mobile pass — `flex-nowrap` keeps pills on one
         row, `overflow-x-auto` lets the user swipe through them on
         very narrow widths (Fold closed). `snap-x snap-mandatory`
         gives a clean swipe-to-pill feel. `scrollbar-hide` strips
         the browser scrollbar so the row looks like a tab strip,
         not a horizontal scroller. */
      className="flex flex-nowrap items-center gap-1.5 sm:gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-1"
    >
      {SECTIONS.map((s, idx) => {
        const isActive = idx === activeIdx;
        const isDone = !!completed[s.slug];
        const isForwardSkip = gateActive && idx > furthestIndex;
        const isDisabled = isForwardSkip || disabled;
        return (
          <button
            key={s.slug}
            type="button"
            onClick={() => {
              if (isDisabled) return;
              onSelect?.(s.slug);
            }}
            disabled={isDisabled}
            data-testid={`workspace-section-nav-${s.slug}`}
            aria-current={isActive ? 'step' : undefined}
            aria-disabled={isDisabled ? 'true' : undefined}
            className={[
              /* RC 1.55 P0 Mobile pass — keep pills compact + ≥36px
                 tap target via `py-1.5`. `snap-start` aligns to the
                 left edge when swiping. `shrink-0` prevents the pill
                 collapsing when the row overflows the viewport. */
              'snap-start shrink-0 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition whitespace-nowrap',
              isActive
                ? 'border-ndor-gold bg-ndor-gold/10 text-ndor-ink shadow-sm'
                : isDisabled
                  ? 'border-ndor-line text-ndor-ink-muted/50 cursor-not-allowed'
                  : 'border-ndor-line text-ndor-ink-muted hover:border-ndor-line-strong hover:text-ndor-ink',
            ].join(' ')}
          >
            {isDone
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden />
              : <s.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            {/* RC 1.59 Slice 4 Mobile pass — short labels below the
                sm: breakpoint (≤ 640px) so all four pills fit on a
                390–430 px Android viewport WITHOUT relying on the
                horizontal scroll fallback. Full labels resume at
                sm: where there's room. The horizontal-scroll +
                snap-x container above remains intact as a safety
                net for the rare even-narrower viewport. */}
            <span className="sm:hidden">{s.shortLabel}</span>
            <span className="hidden sm:inline">{s.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
